// AudioDirector (§11, §13, §25): maps every sim event to SFX, voices, stingers and crowd reactions,
// diffs the stateful map (cake tiles, teacup wedges and tilt, the cup) and runs the per-frame
// loops (teeter wobble, sweeper hum, pinball rumble, cup whirl, potato beeps, bomb ticks, falling
// screams, footsteps, struggle grunts, crown ticks). Captions are produced even before unlock.
import { NO_SLOT, TUNE } from '@shared/constants';
import { EV, HazardKind, KoFlag, type SimEvent } from '@shared/events';
import { BS, Cause, EK, ItemKind, MapId, ModeId, RoomPhase, RoundPhase, TileState } from '@shared/ids';
import { CAKE_GRID } from '@shared/maps/cake';
import type { BeanView, FrameView } from '../game/FrameView';
import type { CrowdDirector } from './crowd/CrowdDirector';
import type { MusicDirector } from './music/MusicDirector';
import { BONK_DEGREES, BONK_DEGREES_MINOR, chainDegree, isMinorScale, nearestWithPc, pc } from './music/theory';
import type { PlayOpts, Voice } from './player';
import { HONKS, SCREAMS, SPLOOSH_BY_MAP, type CrowdKey, type SoundKey } from './sounds';
import { mulberry32 } from './util';
import { SYLLABLES, type BeanLine } from './voice/voices';

/** What the director needs from the engine (implemented by Engine; works before unlock). */
export interface DirectorOut {
  readonly live: boolean;
  play(key: SoundKey, o?: PlayOpts & { caption?: boolean }): Voice | null;
  caption(text: string, sec?: number): void;
  readonly music: MusicDirector | null;
  readonly crowd: CrowdDirector;
  ctxTime(): number;
  /** Context time of a round tick on the view clock (precise), or now. */
  tickTime(tick: number): number;
  /** Whether the shown round tick has come close enough to `tick` to commit a cue (ViewClock.due). */
  tickDue(tick: number, horizonS: number): boolean;
  /** Context time to commit a due cue at `tick` (ViewClock.cueTime), else as tickTime. */
  cueTime(tick: number): number;
  synced(cue: string, tick: number, ctxTime: number): void;
  /** The tonal half of a bonk: an instrument note routed like a positional SFX. */
  bonkTone(midi: number, when: number, o: PlayOpts): void;
  duck(db: number, sec: number): void;
  perfNow(): number;
}

/** Rendered pitches of the in-key SFX (MIDI). */
const CLANG_MIDI = 75.3;
const DING_MIDI = 91; // bumperDing carrier 1568 Hz = G6
const CRATE_POP_MIDI = 69; // pluck 440 Hz
const CROWN_PICK_MIDI = 72; // C major (or minor) arpeggio from C5; crownLost falls to C5 too
const CROWN_POP_MIDI = 96; // C7 sparkle
const POTATO_BEEP_MIDI = 88; // 1320 Hz
const CROWN_TICK_MIDI = 84; // 1046.5 Hz
const COIN_MIDI = 95; // B6 then E7
const BOMB_TICK_MIDI = 83.21; // 1000 Hz

/** Tick-locked cues commit to the audio clock this close to their tick (the fit extrapolates as
 * little as possible); they are flushed every frame and every 25 ms scheduler tick. */
export const COMMIT_S = 0.08;
const BIG_HIT_KB = 12;
const HUGE_HIT_KB = 15;
const STEP_DIST = 0.62;
const GRUNT_EVERY_S = 0.3;
const WOBBLE_MAX = 3;
/** Depth of the void below the rim where a fall ends in a KO, and the scream's sag there. */
const FALL_DEPTH_M = Math.max(1, -TUNE.edge.killZ);
const SCREAM_SAG = 0.1;
/** Crowd clap-along thresholds (excitement). */
const CLAP_START = 0.55;
const CLAP_STOP = 0.4;
const VOICE_LINE_PRI: Readonly<Record<BeanLine, number>> = { hit: 7, ko: 8, stomp: 7, grabbed: 6, hot: 6, throw: 5, teeter: 4, grunt: 2 };
/** A bean does not repeat a line within this long (seconds). */
const LINE_COOLDOWN_S: Readonly<Partial<Record<BeanLine, number>>> = { hit: 0.7, throw: 0.5, teeter: 1.2, grabbed: 0.6, stomp: 0.5, hot: 0.4 };
/** The other beans' shared line budget: a burst of 3, refilled at 3 per second. */
const LINE_BURST = 3;
const LINE_RATE = 3;

const rate = (fromMidi: number, toMidi: number): number => 2 ** ((toMidi - fromMidi) / 12);
/**
 * The bonk transient climbs with the chain at half its tonal half's interval (≤ +8 semitones), so a
 * long chain still lands with weight instead of thinning into a squeak; the tonal half carries the
 * melody (§13.4 pentatonic degrees).
 */
const stepRate = (step: number, minor = false): number => rate(0, chainDegree(step, minor) * 0.5);

interface Bomb { id: number; t0: number; x: number; y: number; next: number }

export class AudioDirector {
  private view: FrameView | null = null;
  private menu = false;
  private readonly rand = mulberry32(0x5eed);
  private readonly prevState = new Array<number>(8).fill(-1);
  private readonly wobbles = new Map<number, Voice>();
  private readonly screams = new Map<number, Voice>();
  private readonly speaking = new Map<number, { voice: Voice; pri: number }>();
  private readonly lastHitBy = new Map<number, { by: number; at: number }>();
  private readonly stepAcc = new Array<number>(8).fill(0);
  private readonly lastPos = Array.from({ length: 8 }, () => ({ x: NaN, y: NaN }));
  private readonly gruntAt = new Array<number>(8).fill(0);
  private readonly bombs = new Map<number, Bomb>();
  private readonly hazardBooked = new Map<string, number>();
  private readonly pendingCues: { key: SoundKey; tick: number; cue: string | null; o: PlayOpts & { caption?: boolean }; offsetSec: number; roundId: number }[] = [];
  private prevTiles: number[] = [];
  private prevWedgeWarn = 0;
  private prevWedgeFallen = 0;
  private prevTilt = 0;
  private creakAt = -Infinity;
  private prevCup = NO_SLOT;
  private cupVoice: Voice | null = null;
  private sweeper: Voice | null = null;
  private prevArm = NaN;
  private readonly rumbles: (Voice | null)[] = [null, null];
  private potatoNext = 0;
  private prevIntensity = 0;
  private readonly crownSec = new Array<number>(8).fill(0);
  private coinStep = 0;
  private doubleTick = Number.NaN;
  private clapping = false;
  private readonly lineAt = new Map<string, number>();
  private lineTokens = LINE_BURST;
  private lineTokenAt = 0;
  private clapNext = 0;
  private sustained = 0;
  private coinAt = -Infinity;
  private mapKey = '';
  /** Frame counter: moving loops update their pan/gain every 3rd frame (≈ 50 ms). */
  private frameNo = 0;

  constructor(private readonly out: DirectorOut) {}

  setMenu(on: boolean): void {
    this.menu = on;
  }

  // ---- Helpers ----------------------------------------------------------------------------------

  private bean(slot: number): BeanView | null {
    const v = this.view;
    if (!v || slot === NO_SLOT || slot < 0 || slot >= v.beans.length) return null;
    const b = v.beans[slot];
    return b.active ? b : null;
  }

  private isLocal(slot: number): boolean {
    return !!this.view && slot !== NO_SLOT && slot === this.view.localSlot;
  }

  /** Positional options for a bean (or a point), flagging the local bean and ghosts. */
  private at(slot: number, extra: PlayOpts = {}, x?: number, y?: number): PlayOpts {
    const b = this.bean(slot);
    const px = x ?? b?.x;
    const py = y ?? b?.y;
    return { x: px, y: py, z: b?.z ?? 0, near: this.isLocal(slot), ghost: b?.ghost ?? false, ...extra };
  }

  private play(key: SoundKey, o: PlayOpts & { caption?: boolean } = {}): Voice | null {
    // The menu diorama is scenery: its sounds are quiet (the player's ambient scale) and uncaptioned.
    return this.out.play(key, this.menu && !o.near ? { ...o, caption: false } : o);
  }

  private caption(text: string, sec?: number): void {
    if (!this.menu) this.out.caption(text, sec);
  }

  /** Music ducking for big moments; the menu diorama never ducks the menu music. */
  private duck(db: number, sec: number): void {
    if (!this.menu) this.out.duck(db, sec);
  }

  /** The score's stingers answer the round, never the menu diorama. */
  private get stage(): MusicDirector | null {
    return this.menu ? null : this.out.music;
  }

  private voicePitch(b: BeanView): number {
    const p = b.voicePitch !== 1 ? b.voicePitch : 0.85 + 0.55 * ((b.slot * 0.618034 + 0.31) % 1);
    return Math.max(0.8, Math.min(1.45, p)) * (b.big ? 0.7 : 1);
  }

  /**
   * A bean voice line (§13.5); a more important line interrupts the bean's current one. An
   * 8-bean brawl would otherwise chatter constantly ("hwah!" on every hit), so a bean does not
   * repeat a line within its cooldown and the other beans share a budget of ~3 lines per second.
   * The local bean's own lines, lines it caused (`important`) and KO calls always speak, and the
   * local bean's lines outrank the others' in the voice pool.
   */
  say(slot: number, line: BeanLine, extra: PlayOpts = {}, important = false): void {
    const b = this.bean(slot);
    if (!b) return;
    const local = this.isLocal(slot);
    const now = this.out.perfNow() / 1000;
    const key = `${slot}:${line}`;
    if (now - (this.lineAt.get(key) ?? -Infinity) < (LINE_COOLDOWN_S[line] ?? 0)) return;
    if (!local && !important && line !== 'ko') {
      this.lineTokens = Math.min(LINE_BURST, this.lineTokens + (now - this.lineTokenAt) * LINE_RATE);
      this.lineTokenAt = now;
      if (this.lineTokens < 1) return;
      this.lineTokens -= 1;
    }
    this.lineAt.set(key, now);
    const pri = VOICE_LINE_PRI[line] + (local ? 2 : 0);
    const cur = this.speaking.get(slot);
    if (cur && !cur.voice.ended) {
      if (cur.pri > pri) return;
      cur.voice.stop(0.03);
    }
    const v = this.play(`line.${line}`, this.at(slot, { rate: this.voicePitch(b), pri, exact: true, ...extra }));
    if (v) this.speaking.set(slot, { voice: v, pri });
  }

  /** Emote gibberish: 3–5 random syllables with a lilting contour. */
  private gibberish(slot: number): void {
    const b = this.bean(slot);
    if (!b) return;
    const n = 3 + Math.floor(this.rand() * 3);
    const t0 = this.out.ctxTime();
    const base = this.voicePitch(b);
    for (let i = 0; i < n; i++) {
      const syl = SYLLABLES[Math.floor(this.rand() * SYLLABLES.length)];
      const contour = 1 + 0.12 * Math.sin((i / Math.max(1, n - 1)) * Math.PI) + (this.rand() - 0.5) * 0.08 - (i === n - 1 ? 0.06 : 0);
      this.play(`syl.${syl}`, this.at(slot, { when: t0 + i * 0.125 + this.rand() * 0.02, rate: base * contour, exact: true, caption: false }));
    }
    this.caption(`${b.name || 'Bean'}: gibberish`, 1.2);
  }

  /** Repitch for an in-key SFX: the key's pitch class nearest to the rendered pitch. */
  private keyed(renderedMidi: number, interval = 0): number {
    const music = this.out.music;
    const tonic = music ? music.tonicAt(this.out.ctxTime()) : 65;
    return rate(renderedMidi, nearestWithPc(renderedMidi, pc(tonic + interval)));
  }

  /** Crowd reactions belong to scoring rounds: not the menu diorama, the lobby or the podium. */
  private react(key: CrowdKey, gain = 1, caption?: string): void {
    const v = this.view;
    if (this.menu || !v || !(v.modeId === ModeId.ROYALE || v.modeId === ModeId.HOT_POTATO || v.modeId === ModeId.CROWN_GRAB)) return;
    this.out.crowd.react(key, gain, caption);
  }

  /** Step `i` of the key's pentatonic (minor in minor and dorian keys), semitones above the root. */
  private pentatonic(i: number): number {
    const d = this.minorKey() ? BONK_DEGREES_MINOR : BONK_DEGREES;
    return d[Math.max(0, Math.min(d.length - 1, i))];
  }

  /** True while a song plays (its grid exists for quantized cues). */
  private grid(): boolean {
    const p = this.out.music?.current;
    return !!p && !p.isStopped;
  }

  private minorKey(): boolean {
    const p = this.out.music?.current;
    return !!p && !p.isStopped && isMinorScale(p.song.def.scale);
  }

  private hitBy(victim: number, by: number): void {
    if (by !== NO_SLOT) this.lastHitBy.set(victim, { by, at: this.out.perfNow() });
  }

  // ---- Events -------------------------------------------------------------------------------------

  onSim(ev: SimEvent, local: boolean, replay: boolean): void {
    const v = this.view;
    if (!v) return;
    // Replays re-dispatch events: the juice replays, the music's round state does not.
    const live = !replay;
    switch (ev.type) {
      case EV.LUNGE:
        this.play('whoosh', this.at(ev.a, { gain: 0.9 }));
        break;
      case EV.HIT:
        this.hit(ev, live);
        break;
      case EV.CLASH: {
        this.play('bonkClick', { x: ev.x, y: ev.y, gain: 0.6, caption: false });
        const when = this.out.music?.quantize(2) ?? this.out.ctxTime();
        this.play('clang', { x: ev.x, y: ev.y, when, rate: this.keyed(CLANG_MIDI, 7), near: this.isLocal(ev.a) || this.isLocal(ev.b) });
        this.react('crowd.ooh', 0.7);
        break;
      }
      case EV.GRAB:
        this.play('yoink', this.at(ev.a));
        this.say(ev.b, 'grabbed', {}, this.isLocal(ev.a));
        this.hitBy(ev.b, ev.a);
        break;
      case EV.GRAB_MISS:
        this.play('whiff', this.at(ev.a));
        break;
      case EV.THROW:
        this.play('yeet', this.at(ev.a));
        this.say(ev.a, 'throw');
        this.hitBy(ev.b, ev.a);
        break;
      case EV.ESCAPE:
        this.play('escapePop', this.at(ev.a));
        break;
      case EV.JUMP: {
        const b = this.bean(ev.a);
        this.play('jump', this.at(ev.a, { rate: b?.big ? 0.72 : 1, caption: false }));
        break;
      }
      case EV.LAND: {
        const b = this.bean(ev.a);
        const impact = ev.d / 10;
        const g = Math.max(0.25, Math.min(1.2, impact / 7));
        if (b?.big) this.play('bigStep', this.at(ev.a, { gain: Math.max(0.6, g), caption: false }));
        else this.play('land', this.at(ev.a, { gain: g, caption: false }, ev.x || undefined, ev.y || undefined));
        break;
      }
      case EV.TEETER:
        this.say(ev.a, 'teeter');
        this.react('crowd.ooh');
        break;
      case EV.TEETER_SAVE:
        if (ev.c === 1) {
          if (live) this.stage?.stinger('clutch');
          this.react('crowd.aah');
          this.react('crowd.cheer', 0.7, 'CLUTCH!');
        }
        break;
      case EV.LEDGE:
        this.say(ev.a, 'throw');
        this.react('crowd.ooh', 0.8);
        break;
      case EV.CLIMB:
        this.play('jump', this.at(ev.a, { gain: 0.6, rate: 0.85, caption: false }));
        break;
      case EV.STOMP:
        this.play('stompCrunch', this.at(ev.b));
        this.play('slideWhistle', this.at(ev.b, { gain: 0.35, rate: 1.25, caption: false }));
        this.say(ev.b, 'stomp', {}, this.isLocal(ev.a));
        this.hitBy(ev.b, ev.a);
        break;
      case EV.FALL:
        this.fall(ev.a);
        break;
      case EV.KO:
        this.ko(ev, live);
        break;
      case EV.BALLOON_SAVE: {
        this.play('balloonStretch', this.at(ev.a));
        if (live) this.stage?.stinger('clutch');
        this.react('crowd.aah');
        const by = this.lastHitBy.get(ev.a);
        if (by && this.out.perfNow() - by.at < 5000) this.angryHonk(by.by);
        break;
      }
      case EV.ITEM_GET:
        // Rendered on C6 → G6: a root-and-fifth chime in the current key.
        this.play('itemGet', this.at(ev.a, { gain: this.isLocal(ev.a) ? 1 : 0.5, caption: this.isLocal(ev.a), rate: this.keyed(84) }));
        break;
      case EV.ITEM_USE:
        this.itemUse(ev.a, ev.b);
        break;
      case EV.CRATE_DROP: {
        this.play('crateWhistle', { x: ev.x, y: ev.y });
        this.play('crateThump', { x: ev.x, y: ev.y, when: this.out.ctxTime() + 1.0 });
        break;
      }
      case EV.CRATE_OPEN: {
        this.play('crateThump', this.at(ev.a, { gain: 0.5, rate: 1.4, caption: false }));
        const when = this.out.music?.quantize(2) ?? this.out.ctxTime();
        this.play(this.minorKey() ? 'cratePopMinor' : 'cratePop', this.at(ev.a, { when, rate: this.keyed(CRATE_POP_MIDI) }));
        break;
      }
      case EV.PEEL_SLIP:
        this.play('slip', this.at(ev.a));
        this.say(ev.a, 'hit');
        this.hitBy(ev.a, ev.b);
        break;
      case EV.BOMB_LAND:
        this.play('crateThump', { x: ev.x, y: ev.y, gain: 0.45, rate: 1.6, caption: false });
        this.play('fuseLight', { x: ev.x, y: ev.y, gain: 0.7 });
        this.bombs.set(ev.c, { id: ev.c, t0: this.out.perfNow(), x: ev.x, y: ev.y, next: 0 });
        break;
      case EV.BOMB_BOOM:
        this.bombs.delete(ev.c);
        this.play('explosion', { x: ev.x, y: ev.y });
        this.duck(-4, 0.3);
        this.react('crowd.aah', 0.8);
        break;
      case EV.PLUNGER_HIT: {
        const t = this.out.ctxTime();
        this.play('plungerThwop', this.at(ev.b));
        this.play('suction', this.at(ev.b, { when: t + 0.1, caption: false }));
        this.play('yank', this.at(ev.b, { when: t + 0.32, caption: false }));
        this.hitBy(ev.b, ev.a);
        break;
      }
      case EV.SHAKE_START:
        this.play('shakeGlug', this.at(ev.a));
        break;
      case EV.SHAKE_END:
        this.play('bwoopDown', this.at(ev.a));
        break;
      case EV.PUNCH_WINDUP:
        this.play('gloveWindup', this.at(ev.a));
        break;
      case EV.PUNCH:
        this.play('whoosh', this.at(ev.a, { rate: 0.8, gain: 1.1 }));
        break;
      case EV.FLOAT_START:
        this.play('balloonStretch', this.at(ev.a));
        break;
      case EV.FLOAT_POP:
        this.play('balloonPop', this.at(ev.a));
        break;
      case EV.PEBBLE_DROP:
        this.play('pebbleDrop', { x: ev.x, y: ev.y, ghost: true, near: this.isLocal(ev.a) });
        break;
      case EV.PEBBLE_HIT:
        this.play('pebbleTok', this.at(ev.b, { ghost: true }));
        this.hitBy(ev.b, ev.a);
        break;
      case EV.HONK:
        this.honk(ev.a, 1);
        break;
      case EV.EMOTE:
        this.gibberish(ev.a);
        break;
      case EV.PHASE:
        if (!live) break;
        if (ev.b === RoundPhase.RAMP) {
          this.out.caption('HAZARDS UP!', 1.6);
          this.react('crowd.ooh', 0.6);
        } else if (ev.b === RoundPhase.SUDDEN_DEATH) {
          // Its key and tempo are read just inside Sudden Death (the phase starts on this tick).
          const at = Math.max(this.out.ctxTime(), this.out.tickTime(ev.tick));
          this.out.music?.stingerAt('suddenDeathSiren', at, at + 0.1);
          this.react('crowd.gasp');
        }
        break;
      case EV.HAZARD_WARN:
        this.hazardWarn(ev);
        break;
      case EV.HAZARD_FIRE:
        this.hazardFire(ev);
        break;
      case EV.CROWN_POP:
        this.play(this.minorKey() ? 'crownPopMinor' : 'crownPop', { x: ev.x, y: ev.y, rate: this.keyed(CROWN_POP_MIDI) });
        this.react('crowd.ooh', 0.7);
        break;
      case EV.CROWN_RESPAWN:
        // A crown lost over the void drops back in (x, y): a rising sparkle where it lands, in key.
        this.play(this.minorKey() ? 'regrowSparkleMinor' : 'regrowSparkle', { x: ev.x, y: ev.y, rate: this.keyed(96), gain: 1.3, caption: false });
        this.out.caption('The crown is back!', 1.4);
        this.react('crowd.ooh', 0.5);
        break;
      case EV.CROWN_PICK: {
        this.play('pebbleTok', this.at(ev.a, { gain: 0.4, caption: false }));
        const when = this.out.music?.quantize(2) ?? this.out.ctxTime();
        this.play(this.minorKey() ? 'crownPickMinor' : 'crownPick', this.at(ev.a, { when, rate: this.keyed(CROWN_PICK_MIDI) }));
        break;
      }
      case EV.CROWN_LOST:
        // A fall from the key's 3rd (major or minor) to its root.
        this.play(this.minorKey() ? 'crownLostMinor' : 'crownLost', { rate: this.keyed(CROWN_PICK_MIDI) });
        break;
      case EV.POTATO_NEW:
        this.play('fuseLight', this.at(ev.a));
        this.say(ev.a, 'hot');
        break;
      case EV.POTATO_PASS:
        this.play('potatoPass', this.at(ev.b));
        this.say(ev.b, 'hot');
        break;
      case EV.POTATO_BOOM:
        this.play('potatoBoom', { x: ev.x, y: ev.y });
        this.duck(-4, 0.3);
        this.react('crowd.aah');
        break;
      case EV.RESPAWN:
        this.play('parachute', this.at(ev.a, {}, ev.x, ev.y));
        break;
      case EV.MATCH_POINT:
        if (live) this.stage?.stinger('matchPoint');
        this.react('crowd.ooh');
        break;
      case EV.ROUND_OVER:
        if (!live) break;
        if (ev.a === 255) {
          this.play('ann.draw', { when: this.out.ctxTime() + 0.2 });
          this.react('crowd.laugh');
        } else {
          this.react('crowd.cheer');
        }
        this.out.music?.roundOver();
        break;
      case EV.BUMPER: {
        const tonic = this.out.music?.tonicAt(this.out.ctxTime()) ?? 65;
        const target = 84 + pc(tonic + 7);
        this.play('bumperDing', this.at(ev.a, { rate: rate(DING_MIDI, target) }));
        this.hitBy(ev.a, NO_SLOT);
        break;
      }
      case EV.SPUN:
        this.play('headBoing', this.at(ev.a, { gain: 0.6, rate: 1.3, caption: false }));
        break;
      case EV.FLUNG:
        this.play('fling', this.at(ev.a));
        break;
      case EV.DUMMY_HIT: {
        const m = this.out.music?.bonk(!live);
        const pos = { x: ev.x, y: ev.y };
        this.play('bonk', { ...pos, gain: 0.85, rate: stepRate(m?.step ?? 0, this.minorKey()), exact: true });
        if (m) this.out.bonkTone(m.midi, m.when, { ...pos, gain: 0.55, lowpass: 3000 + 60 * Math.min(100, ev.d) });
        this.play('headBoing', { ...pos, gain: 0.4, caption: false, when: this.out.ctxTime() + 0.05 });
        break;
      }
      case EV.GHOST_READY:
        this.play('ghostWhoo', this.at(ev.a, { ghost: true }));
        if (this.isLocal(ev.a)) this.out.caption('You’re a ghost! (shove = drop pebble)', 2.5);
        break;
      case EV.COUNTDOWN:
        if (live) this.out.music?.countdownEvent(ev.b, ev.tick);
        break;
      case EV.DETHRONE:
        this.play(this.minorKey() ? 'crownPopMinor' : 'crownPop', this.at(ev.a, { rate: this.keyed(CROWN_POP_MIDI) }));
        this.react('crowd.ooh');
        this.react('crowd.laugh', 0.7);
        break;
      case EV.READY_PAD:
        if (ev.b === 1) this.coin(this.at(ev.a, { gain: this.isLocal(ev.a) ? 1 : 0.6 }));
        break;
      case EV.VEND:
        this.play('vendClunk', this.at(ev.a));
        break;
      case EV.HEAD_BONK:
        this.play('headBoing', this.at(ev.a));
        this.say(ev.a, 'hit');
        break;
      default:
        break;
    }
    void local;
  }

  private hit(ev: SimEvent, live: boolean): void {
    const victim = this.bean(ev.b);
    const kb = ev.d / 10;
    const dizzy = victim?.dizzy ?? 0;
    const near = this.isLocal(ev.a) || this.isLocal(ev.b);
    const pos: PlayOpts = { x: ev.x, y: ev.y, z: victim?.z ?? 0, near };
    // Dizzy brightens the bonk: a low-pass opening from 3 kHz to 9 kHz.
    const lowpass = 3000 + 6000 * Math.max(0, Math.min(1, dizzy / 100));
    const cause = ev.c as Cause;
    const m = this.out.music?.bonk(!live);
    const blast = cause === Cause.BOMB || cause === Cause.POTATO;
    const sr = stepRate(m?.step ?? 0, this.minorKey());
    const g = Math.max(0.55, Math.min(1.25, 0.55 + kb / 14));
    if (cause === Cause.GLOVE) {
      this.play('pow', { ...pos, lowpass: lowpass + 3000, rate: sr, exact: true });
    } else if (!blast) {
      // The transient plays now (pitched by the chain); the tonal half lands on the next 16th.
      this.play('bonk', { ...pos, gain: g, lowpass, rate: sr * (victim?.big ? 0.8 : 1), exact: true });
    }
    if (m) this.out.bonkTone(m.midi, m.when, { ...pos, gain: blast ? 0.45 : 0.75 * g, lowpass: lowpass + 2000 });
    if (kb >= HUGE_HIT_KB || dizzy >= 85) this.play('subBoom', { ...pos, gain: 0.9, caption: false });
    if (kb >= BIG_HIT_KB || dizzy >= 85) this.duck(-4, 0.3);
    this.say(ev.b, 'hit', {}, near);
    this.hitBy(ev.b, ev.a);
  }

  private fall(slot: number): void {
    const b = this.bean(slot);
    if (!b) return;
    const id = SCREAMS[b.cosmetics.scream] ?? 'slideWhistle';
    this.screams.get(slot)?.stop(0.05);
    const v = this.play(id, this.at(slot, { rate: this.voicePitch(b) / (b.big ? 0.85 : 1) * 0.92 + 0.08 }));
    if (v) this.screams.set(slot, v);
  }

  private ko(ev: SimEvent, live: boolean): void {
    const v = this.view as FrameView;
    const flags = ev.d;
    const scream = this.screams.get(ev.a);
    if (scream) {
      scream.stop(0.2);
      this.screams.delete(ev.a);
    }
    const sploosh = SPLOOSH_BY_MAP[v.mapId] ?? 'splooshLilac';
    const near = this.isLocal(ev.a) || this.isLocal(ev.b);
    this.play(sploosh, { x: ev.x, y: ev.y, near, pri: 10 });
    this.duck(-4, 0.3);
    const self = (flags & KoFlag.SELF) !== 0 || ev.b === NO_SLOT;
    // A double bonk is several KO events on one tick (each with its own sploosh): announce it once.
    if (flags & KoFlag.DOUBLE && ev.tick !== this.doubleTick) {
      this.doubleTick = ev.tick;
      this.play('ann.doubleBonk', { when: this.out.ctxTime() + 0.25 });
      this.react('crowd.laugh');
    }
    const finalOfRound = (flags & KoFlag.FINAL) !== 0;
    const lastRound = v.totalRounds > 0 && v.roundIndex >= v.totalRounds - 1;
    const music = this.stage;
    // The round's music state (tape stop, GAME!) only follows live KOs; replays replay the juice.
    if (live && finalOfRound) {
      music?.tapeStop();
      if (lastRound || v.golden) {
        music?.stingerAt('gameStinger', this.out.ctxTime() + 0.35);
        this.react('crowd.roar');
      }
    }
    // Every KO ends the bonk phrase (§13.4); the credited ones resolve it on the root.
    if (self) music?.stinger('oops');
    else if (flags & KoFlag.REVENGE) music?.stinger('revenge');
    else if (flags & KoFlag.BOUNTY) music?.stinger('bounty');
    else music?.koResolve(!live);
    this.out.music?.resetChain(!live);
    if (self) {
      this.react('crowd.laugh', 1, 'Crowd: ho ho ho (OOPS!)');
    } else {
      this.react('crowd.aah');
      this.react('crowd.cheer', 0.8);
      const credit = this.bean(ev.b);
      if (credit) this.say(ev.b, 'ko', { ghost: credit.ghost || (flags & KoFlag.GHOST_KO) !== 0 });
    }
  }

  private honk(slot: number, gain: number): void {
    const b = this.bean(slot);
    if (!b) return;
    const id = HONKS[b.cosmetics.honk] ?? 'clownHorn';
    this.play(id, this.at(slot, { gain, rate: b.big ? 0.7 : 1 }));
  }

  private angryHonk(slot: number): void {
    const b = this.bean(slot);
    if (!b) return;
    const id = HONKS[b.cosmetics.honk] ?? 'clownHorn';
    const t = this.out.ctxTime() + 0.25;
    this.play(id, this.at(slot, { when: t, rate: 1.12, exact: true }));
    this.play(id, this.at(slot, { when: t + 0.16, rate: 1.19, exact: true, caption: false }));
    this.caption('Angry honk!', 1.2);
  }

  private itemUse(slot: number, item: number): void {
    switch (item) {
      case ItemKind.BANANA: this.play('peelSplat', this.at(slot, { caption: false })); break;
      case ItemKind.BOMB: this.play('whoosh', this.at(slot, { rate: 0.85 })); break;
      case ItemKind.GLOVE: break; // PUNCH_WINDUP / PUNCH carry it
      case ItemKind.BALLOON: this.play('balloonStretch', this.at(slot)); break;
      case ItemKind.PLUNGER: this.play('whoosh', this.at(slot, { rate: 1.2, gain: 0.8 })); break;
      case ItemKind.SHAKE: this.play('shakeGlug', this.at(slot)); break;
      default: break;
    }
  }

  /** UI coin chime: consecutive calls step up a pentatonic scale (score tally chips). */
  coin(o: PlayOpts = {}): void {
    const now = this.out.perfNow();
    this.coinStep = now - this.coinAt < 1500 ? Math.min(this.coinStep + 1, 7) : 0;
    this.coinAt = now;
    const deg = this.pentatonic(this.coinStep);
    this.play('coin', { ...o, rate: this.keyed(COIN_MIDI) * rate(0, deg) / rate(0, 12) });
  }

  // ---- Hazards: tick-locked (§29.4 sync) ----------------------------------------------------------

  private book(kind: number, index: number, tick: number): boolean {
    const key = `${kind}:${index}:${Math.round(tick)}`;
    if (this.hazardBooked.has(key)) return false;
    // Prune old bookings.
    if (this.hazardBooked.size > 64) for (const [k, t] of this.hazardBooked) if (t < tick - 600) this.hazardBooked.delete(k);
    this.hazardBooked.set(key, tick);
    return true;
  }

  private booked(kind: number, index: number, tick: number): boolean {
    for (let d = -3; d <= 3; d++) if (this.hazardBooked.has(`${kind}:${index}:${Math.round(tick) + d}`)) return true;
    return false;
  }

  /**
   * A tick-locked cue. It is committed to the audio clock only when its tick is within
   * `COMMIT_S`, with the freshest tick → time mapping (long telegraph leads stay exact).
   */
  private cueAt(key: SoundKey, tick: number, cue: string | null, o: PlayOpts & { caption?: boolean }, offsetSec = 0): void {
    this.pendingCues.push({ key, tick, cue, o, offsetSec, roundId: this.view?.roundId ?? 0 });
    this.flushCues();
  }

  /** Commits due tick-locked cues (also called from the 25 ms scheduler tick). */
  flushCues(): void {
    if (this.pendingCues.length === 0) return;
    const now = this.out.ctxTime();
    const roundId = this.view?.roundId ?? 0;
    for (let i = 0; i < this.pendingCues.length; i++) {
      const c = this.pendingCues[i];
      if (c.roundId !== roundId) {
        this.pendingCues.splice(i--, 1);
        continue;
      }
      if (this.out.tickTime(c.tick) + c.offsetSec - now > COMMIT_S || !this.out.tickDue(c.tick + c.offsetSec * 60, COMMIT_S)) continue;
      const at = this.out.cueTime(c.tick) + c.offsetSec;
      this.pendingCues.splice(i--, 1);
      const when = Math.max(now, at);
      this.play(c.key, { ...c.o, when });
      if (c.cue) this.out.synced(c.cue, c.tick, when);
    }
  }

  private hazardWarn(ev: SimEvent): void {
    const fireTick = ev.tick + ev.d;
    const pos = { x: ev.x, y: ev.y };
    const v = this.view as FrameView;
    const beatTicks = v.clock.tpb;
    switch (ev.b) {
      case HazardKind.FLIPPER:
        if (!this.book(ev.b, ev.c, fireTick)) return;
        // The lights blink on the 2 beats before the fire, then the clack lands on the fire tick.
        for (const k of [2, 1]) if (fireTick - k * beatTicks >= ev.tick - 2) this.cueAt('flipperBlink', fireTick - k * beatTicks, null, { ...pos, caption: false });
        this.cueAt('flipperClack', fireTick, 'flipper', pos);
        break;
      case HazardKind.PINBALL_LAUNCH:
        this.play('ballAlarm', pos);
        if (this.book(ev.b, ev.c, fireTick)) this.cueAt('pinballLaunch', fireTick, 'pinball', pos);
        break;
      case HazardKind.CANDLE:
        if (!this.book(ev.b, ev.c, fireTick)) return;
        // The flicker swell peaks on the puff.
        this.cueAt('candleFlicker', fireTick, null, pos, -0.95);
        this.cueAt('flameWhoosh', fireTick, 'candle', pos);
        break;
      case HazardKind.SWEEPER_REVERSE:
        this.cueAt('reverseBuzzer', ev.tick, null, { pan: 0 });
        if (this.book(ev.b, ev.c, fireTick)) this.cueAt('sweeperClunk', fireTick, 'sweeper', { pan: 0 });
        break;
      case HazardKind.THIRD_ARM:
        this.out.caption('Third arm incoming!', 1.6);
        if (this.book(ev.b, ev.c, fireTick)) this.cueAt('armDrop', fireTick, null, { pan: 0 }, -0.28);
        break;
      case HazardKind.BUBBLE:
        this.play('bubbleWobble', pos);
        if (this.book(ev.b, ev.c, fireTick)) this.cueAt('bubblePop', fireTick, 'bubble', pos);
        break;
      default:
        break; // WEDGE and TILE_SD are presented from the map state diff (§20.10).
    }
  }

  private hazardFire(ev: SimEvent): void {
    if (this.booked(ev.b, ev.c, ev.tick)) return;
    this.book(ev.b, ev.c, ev.tick);
    const pos = { x: ev.x, y: ev.y };
    switch (ev.b) {
      case HazardKind.FLIPPER: this.cueAt('flipperClack', ev.tick, 'flipper', pos); break;
      case HazardKind.PINBALL_LAUNCH: this.cueAt('pinballLaunch', ev.tick, 'pinball', pos); break;
      case HazardKind.CANDLE: this.cueAt('flameWhoosh', ev.tick, 'candle', pos); break;
      case HazardKind.SWEEPER_REVERSE: this.cueAt('sweeperClunk', ev.tick, 'sweeper', { pan: 0 }); break;
      case HazardKind.THIRD_ARM: this.cueAt('armDrop', ev.tick, null, { pan: 0 }); break;
      case HazardKind.BUBBLE: this.cueAt('bubblePop', ev.tick, 'bubble', pos); break;
      default: break;
    }
  }

  // ---- Per frame ----------------------------------------------------------------------------------

  update(view: FrameView): void {
    this.view = view;
    this.frameNo++;
    const key = `${view.mapId}:${view.roundId}:${view.source === 'replay' ? 'r' : 'l'}`;
    if (key !== this.mapKey) {
      this.mapKey = key;
      this.resetMapState(view);
    }
    if (!this.out.live) return;
    this.flushCues();
    const now = this.out.perfNow();
    const inRound = view.roomPhase === RoomPhase.INTRO || view.roomPhase === RoomPhase.PLAY || view.roomPhase === RoomPhase.LOBBY || view.roomPhase === RoomPhase.PODIUM || view.roomPhase === RoomPhase.ROUND_END;
    this.beansFrame(view, now);
    this.mapDiff(view);
    this.loops(view, inRound);
    this.potato(view);
    this.bombTicks(view, now);
    this.crownTicks(view);
    this.clapAlong(view);
  }

  /**
   * When a round stays tense (sustained excitement ≥ 0.55: a teeter in Sudden Death, match point,
   * the last few beans) the stands clap along on 2 and 4, locked to the score, louder the more
   * excited they are. The decision follows a slow (1.2 s) average, so a burst of KOs does not
   * start a clap that dies a beat later; they stop below 0.4 (hysteresis, so it never flickers).
   */
  private clapAlong(view: FrameView): void {
    this.sustained += (view.excitement - this.sustained) * (1 - Math.exp(-Math.max(0, view.dt) / 1.2));
    const x = this.sustained;
    const allowed = !this.menu && view.source !== 'replay' && view.roomPhase === RoomPhase.PLAY
      && (view.modeId === ModeId.ROYALE || view.modeId === ModeId.HOT_POTATO || view.modeId === ModeId.CROWN_GRAB);
    const was = this.clapping;
    this.clapping = allowed && (was ? x > CLAP_STOP : x >= CLAP_START);
    if (!this.clapping) return;
    const music = this.out.music;
    if (!music) return;
    const now = this.out.ctxTime();
    if (this.clapNext > now + 0.15) return;
    const when = music.nextBackbeat(Math.max(now, this.clapNext + 0.05));
    if (!Number.isFinite(when) || when - now > 1.5) return;
    if (!was) this.caption('Crowd claps along', 2);
    const gain = 0.45 + 0.55 * Math.min(1, Math.max(0, (view.excitement - CLAP_STOP) / (1 - CLAP_STOP)));
    this.out.crowd.clap(when, gain);
    this.clapNext = when;
  }

  private resetMapState(view: FrameView): void {
    this.prevTiles = [...view.map.tileState];
    this.prevWedgeWarn = view.map.wedgeWarn;
    this.prevWedgeFallen = view.map.wedgeFallen;
    this.prevCup = view.map.cupOccupant;
    this.prevArm = NaN;
    this.bombs.clear();
    for (const w of this.wobbles.values()) w.stop(0.1);
    this.wobbles.clear();
    this.prevState.fill(-1);
  }

  private beansFrame(view: FrameView, now: number): void {
    const replay = view.source === 'replay';
    for (const b of view.beans) {
      const s = b.slot;
      const prev = this.prevState[s];
      this.prevState[s] = b.active ? b.state : -1;
      // Teeter wobble loop, following the bean.
      const wob = this.wobbles.get(s);
      const edge = b.active && (b.state === BS.TEETER || b.state === BS.LEDGE) && !b.out;
      if (edge && !wob && this.wobbles.size < WOBBLE_MAX) {
        const v = this.play('teeterWobble', this.at(s, { gain: b.state === BS.LEDGE ? 0.6 : 1, loop: true, caption: prev !== b.state }));
        if (v) this.wobbles.set(s, v);
      } else if (!edge && wob) {
        wob.stop(0.12);
        this.wobbles.delete(s);
      } else if (wob && (this.frameNo + s) % 3 === 0) {
        wob.setPos(b.x, b.y, b.z);
      }
      // Falling screams follow the bean: panned with it, receding as it drops below the rim, and
      // sagging in pitch as it falls away (up to −10 % at the kill depth, a Doppler-ish droop).
      const scream = this.screams.get(s);
      if (scream) {
        if (scream.ended) this.screams.delete(s);
        else if (b.active && (this.frameNo + s) % 3 === 0) {
          scream.setPos(b.x, b.y, b.z);
          const sag = 1 - SCREAM_SAG * Math.min(1, Math.max(0, -b.z) / FALL_DEPTH_M);
          scream.src.playbackRate.setTargetAtTime(scream.baseRate * sag, scream.src.context.currentTime, 0.08);
        }
      }
      if (!b.active || b.out || replay) {
        this.lastPos[s].x = NaN;
        continue;
      }
      // Footsteps: a quiet tick every ~0.6 m walked on the ground.
      const lp = this.lastPos[s];
      if (Number.isFinite(lp.x) && b.state === BS.NORMAL && b.z <= 0.05) {
        const d = Math.hypot(b.x - lp.x, b.y - lp.y);
        if (d < 1) this.stepAcc[s] += d;
        const stride = STEP_DIST * (b.big ? 1.6 : 1);
        if (this.stepAcc[s] >= stride) {
          this.stepAcc[s] = 0;
          const local = this.isLocal(s);
          if (local || this.rand() < 0.5) this.play(b.big ? 'bigStep' : 'step', this.at(s, { gain: b.big ? 0.5 : local ? 1 : 0.6, caption: false }));
        }
      } else {
        this.stepAcc[s] = 0;
      }
      lp.x = b.x;
      lp.y = b.y;
      // Struggle grunts while carried.
      if (b.state === BS.GRABBED && now - this.gruntAt[s] > GRUNT_EVERY_S * 1000) {
        this.gruntAt[s] = now + this.rand() * 80;
        this.say(s, 'grunt', { caption: false });
      }
    }
  }

  private mapDiff(view: FrameView): void {
    const m = view.map;
    if (view.mapId === MapId.CAKE && m.tileState.length > 0) {
      let budget = 4;
      for (let i = 0; i < m.tileState.length; i++) {
        const s = m.tileState[i];
        const p = this.prevTiles[i];
        if (s === p) continue;
        this.prevTiles[i] = s;
        if (budget <= 0 || p === undefined) continue;
        const x = CAKE_GRID.cx[i] ?? 0;
        const y = CAKE_GRID.cy[i] ?? 0;
        if (s === TileState.CRACKING) this.play('tileCrack', { x, y });
        else if (s === TileState.FALLING) this.play('tileFall', { x, y, caption: false });
        else if (s === TileState.REGROW) this.play(this.minorKey() ? 'regrowSparkleMinor' : 'regrowSparkle', { x, y, rate: this.keyed(96), caption: false });
        else continue;
        budget--;
      }
    }
    if (view.mapId === MapId.TEACUP) {
      const newWarn = m.wedgeWarn & ~this.prevWedgeWarn;
      const newFall = m.wedgeFallen & ~this.prevWedgeFallen;
      for (let k = 0; k < 8; k++) {
        const a = (k * Math.PI) / 4;
        const x = 6 * Math.cos(a);
        const y = 6 * Math.sin(a);
        if (newWarn & (1 << k)) this.play('wedgeCrack', { x, y });
        if (newFall & (1 << k)) this.play('ceramicClatter', { x, y });
      }
      this.prevWedgeWarn = m.wedgeWarn;
      this.prevWedgeFallen = m.wedgeFallen;
      const tilt = Math.hypot(m.tiltX, m.tiltY);
      const limit = (8 * Math.PI) / 180;
      const now = this.out.perfNow();
      if (tilt > limit && this.prevTilt <= limit && now - this.creakAt > 1500) {
        this.creakAt = now;
        const len = tilt > 0 ? 6 / tilt : 0;
        this.play('porcelainCreak', { x: m.tiltX * len, y: m.tiltY * len });
      }
      this.prevTilt = tilt;
      // The spinning cup: whirl while occupied.
      if (m.cupOccupant !== this.prevCup) {
        this.prevCup = m.cupOccupant;
        this.cupVoice?.stop(0.25);
        this.cupVoice = m.cupOccupant !== NO_SLOT ? this.play('cupWhirl', { x: 0, y: 0, loop: true, near: this.isLocal(m.cupOccupant) }) : null;
      }
    } else if (this.cupVoice) {
      this.cupVoice.stop(0.2);
      this.cupVoice = null;
    }
  }

  private loops(view: FrameView, inRound: boolean): void {
    const h = view.hazard;
    // Spin Cycle sweeper hum: pitch and brightness track |ω| (§13.3: cutoff = 200 + 40·|ω|).
    const spin = inRound && view.mapId === MapId.SPIN && h.armCount > 0;
    if (spin) {
      const ang = h.armAngles[0];
      let omega = 0;
      if (Number.isFinite(this.prevArm) && view.dt > 0) {
        let d = ang - this.prevArm;
        while (d > Math.PI) d -= 2 * Math.PI;
        while (d < -Math.PI) d += 2 * Math.PI;
        omega = Math.abs(d / view.dt) * (180 / Math.PI);
      }
      this.prevArm = ang;
      if (!this.sweeper) this.sweeper = this.play('sweeperHum', { pan: 0, loop: true, lowpass: 400, caption: false });
      if (this.sweeper && omega > 0 && omega < 400 && this.frameNo % 3 === 0) {
        const t = this.out.ctxTime();
        this.sweeper.src.playbackRate.setTargetAtTime(0.8 + omega / 225, t, 0.1);
        this.sweeper.filter?.frequency.setTargetAtTime(200 + 40 * omega, t, 0.1);
      }
    } else if (this.sweeper) {
      this.sweeper.stop(0.3);
      this.sweeper = null;
      this.prevArm = NaN;
    }
    // Pinball rumbles follow each giant ball.
    for (let i = 0; i < this.rumbles.length; i++) {
      const ball = inRound && view.mapId === MapId.PINBALL ? h.balls[i] : undefined;
      const r = this.rumbles[i];
      if (ball?.active) {
        if (!r) this.rumbles[i] = this.play('pinballRumble', { x: ball.x, y: ball.y, loop: true, caption: i === 0 });
        else if ((this.frameNo + i) % 3 === 0) r.setPos(ball.x, ball.y);
      } else if (r) {
        r.stop(0.3);
        this.rumbles[i] = null;
      }
    }
  }

  /** Potato beeps on the music grid: quarter → 8th → 16th notes as the intensity rises. */
  private potato(view: FrameView): void {
    const slot = view.mode.potatoSlot;
    const live = view.modeId === ModeId.HOT_POTATO && slot !== NO_SLOT && view.roomPhase === RoomPhase.PLAY;
    const intensity = live ? view.mode.potatoIntensity : 0;
    if (live && intensity >= 13 && this.prevIntensity < 13) this.react('crowd.gasp');
    this.prevIntensity = intensity;
    if (!live) return;
    const now = this.out.ctxTime();
    if (now + 0.05 < this.potatoNext) return;
    const unit = intensity >= 12 ? 1 : intensity >= 8 ? 2 : intensity >= 4 ? 4 : 8;
    // On the score's grid, searching past the beep already booked (they are booked up to 50 ms
    // ahead); with no song, at the same spacing on the clock.
    const when = this.grid() ? (this.out.music as MusicDirector).quantize(unit, this.potatoNext) : Math.max(now, this.potatoNext - 0.02 + unit * 0.11);
    const deg = this.pentatonic(Math.min(5, Math.floor(intensity / 3)));
    this.play('potatoBeep', this.at(slot, { when, rate: this.keyed(POTATO_BEEP_MIDI) * rate(0, deg), gain: 0.6 + intensity / 30, caption: false }));
    this.out.caption('Hot potato ticking', 0.6);
    this.potatoNext = when + 0.02;
  }

  private bombTicks(view: FrameView, nowMs: number): void {
    if (this.bombs.size === 0) return;
    const now = this.out.ctxTime();
    for (const bomb of this.bombs.values()) {
      const ent = view.ents.find((e) => e.id === bomb.id && e.kind === EK.BOMB);
      const elapsed = (nowMs - bomb.t0) / 1000;
      if (elapsed > 3) {
        this.bombs.delete(bomb.id);
        continue;
      }
      if (ent) {
        bomb.x = ent.x;
        bomb.y = ent.y;
      }
      if (now + 0.05 < bomb.next) continue;
      // §11.3: ticks accelerate over the fuse, on the score's grid (8ths, then 16ths, then 32nds in
      // the last half second), climbing the key's pentatonic as it burns down.
      const fuse = TUNE.bomb.fuseTicks / 60;
      const left = fuse - elapsed;
      if (left <= 0.03) continue;
      const unit = left > 1 ? 2 : left > 0.45 ? 1 : 0.5;
      const when = this.grid() ? (this.out.music as MusicDirector).quantize(unit, bomb.next) : Math.max(now, bomb.next - 0.02 + unit * 0.11);
      if (when - now > left) {
        bomb.next = now + left + 1;
        continue;
      }
      const deg = this.pentatonic(Math.min(7, Math.floor((1 - left / fuse) * 7)));
      this.play('bombTick', { x: bomb.x, y: bomb.y, when, gain: 0.8, rate: this.keyed(BOMB_TICK_MIDI) * rate(0, deg), caption: bomb.next === 0 });
      bomb.next = when + 0.02;
    }
  }

  /** Crown Grab: a soft tick per crown-second, rising as the holder nears the goal. */
  private crownTicks(view: FrameView): void {
    if (view.modeId !== ModeId.CROWN_GRAB) return;
    const goal = Math.max(1, view.mode.crownGoalTicks || 1500);
    for (const b of view.beans) {
      if (!b.active) continue;
      const sec = Math.floor(b.crownTicks / 60);
      if (b.holdsCrown && sec > this.crownSec[b.slot]) {
        const progress = Math.min(1, b.crownTicks / goal);
        const deg = this.pentatonic(Math.min(7, Math.floor(progress * 8)));
        // On the next 16th, so the count grooves with the score like the potato and the bomb.
        const when = this.grid() ? (this.out.music as MusicDirector).quantize(1) : undefined;
        this.play('crownTick', this.at(b.slot, { when, rate: this.keyed(CROWN_TICK_MIDI) * rate(0, deg), gain: this.isLocal(b.slot) ? 1 : 0.6 }));
      }
      this.crownSec[b.slot] = sec;
    }
  }
}

export { SPLOOSH_BY_MAP };
